import { getName } from "country-list";
import { getWorldCupNations } from "../services/worldCupNation.service";
import { LEAGUE_NAME_KEYWORDS } from "./constants";

const toNationOption = ({ id, shortName, name, code }) => ({
  id,
  shortName: String(shortName || "").toUpperCase(),
  name: String(name || ""),
  code: String(code || "").toUpperCase(),
});

export const getWorldCupNationsForTable = (
  page,
  perPage,
  search = "",
  options = {},
) => getWorldCupNations(page, perPage, search, options);

export const fetchWorldCupNationMap = async () => {
  const map = new Map();
  let page = 0;
  const perPage = 100;
  let totalCount = 0;

  do {
    const result = await getWorldCupNations(page, perPage, "", {
      sortOrder: "ASC",
    });
    totalCount = result.totalCount ?? 0;

    for (const nation of result.items || []) {
      map.set(String(nation.shortName).toUpperCase(), nation);
    }

    page += 1;
  } while (page * perPage < totalCount);

  return map;
};

export const findWorldCupNationByShortName = async (shortName) => {
  const key = String(shortName || "")
    .trim()
    .toUpperCase();
  if (!key) {
    return null;
  }

  const { items } = await getWorldCupNations(0, 20, key, {
    sortOrder: "ASC",
  });

  return (
    (items || []).find(
      (nation) => String(nation.shortName).toUpperCase() === key,
    ) || null
  );
};

export const searchWorldCupNations = async (search = "") => {
  const term = String(search || "").trim();
  if (term.length < 2) {
    return [];
  }

  const { items } = await getWorldCupNations(0, 20, term, {
    sortOrder: "ASC",
  });

  return (items || [])
    .filter((nation) => nation.isActive !== false)
    .map(toNationOption);
};

export const isWorldCupLeague = (leagueName) =>
  String(leagueName || "")
    .toLowerCase()
    .includes(LEAGUE_NAME_KEYWORDS.WORLD_CUP);

export const formatWorldCupNationLabel = (nation) => {
  if (!nation) {
    return "";
  }

  const shortName = String(nation.shortName || nation).toUpperCase();
  const name = String(nation.name || "").trim();
  return name ? `${shortName} — ${name}` : shortName;
};

export const formatWorldCupNationOptionLabel = (option) => {
  if (!option) {
    return "";
  }

  if (option.shortName) {
    return formatWorldCupNationLabel(option);
  }

  return option.name || "";
};

export const resolveWorldCupNationDisplay = (shortName, nationMap) => {
  const key = String(shortName || "").toUpperCase();
  if (!key) {
    return null;
  }

  const nation = nationMap?.get?.(key);
  if (nation) {
    return formatWorldCupNationLabel(nation);
  }

  return key;
};

export const resolveCountryMatchDisplay = (
  countryValue,
  leagueName,
  nationMap,
) => {
  if (!countryValue) {
    return null;
  }

  if (isWorldCupLeague(leagueName)) {
    return resolveWorldCupNationDisplay(countryValue, nationMap);
  }

  return (
    getName(String(countryValue).toLowerCase()) ||
    String(countryValue).toUpperCase()
  );
};

export const resolveCountryEventDisplay = (
  countryShortName,
  nationMap,
  homeSide,
  awaySide,
) => {
  const key = String(countryShortName || "").toUpperCase();
  if (!key) {
    return "N/A";
  }

  if (homeSide?.code && key === String(homeSide.code).toUpperCase()) {
    return homeSide.label || key;
  }

  if (awaySide?.code && key === String(awaySide.code).toUpperCase()) {
    return awaySide.label || key;
  }

  return resolveWorldCupNationDisplay(key, nationMap) || key;
};
